import React from "react";
import styles from "./PDFExport.module.scss";
import { PesTest, TrainedIndicator } from "generated/graphql";
import { PartialDeep } from "type-fest";
import { format, parseISO } from "date-fns";
import { IndicatorGradientColors } from "@pages/pdf-export/PDFExport";
import tinygradient from "tinygradient";
import {
  GetDynamicValue,
  GetPESIndex,
  IndicatorNameMatches,
} from "@pages/pdf-export/Indices";
export interface PdfIndicatorHeader {
  id: string;
  displayName: string;
  name: string;
}

interface Page1TableProps {
  allTests: PartialDeep<PesTest, { recurseIntoArrays: true }>[];
  indicatorNamesAndIds: {
    id: string;
    name: string;
    displayName: string;
    trainedIndicator: PartialDeep<TrainedIndicator>;
  }[];
  /** Headers to render; defaults to every biological indicator. */
  headers?: PdfIndicatorHeader[];
}

export const page1Headers: PdfIndicatorHeader[] = [
  {
    id: "microbialBiomass",
    displayName: "Microbial Biomass",
    name: "Microbial Biomass",
  },
  {
    id: "basalRespirationRate",
    displayName: "Basal Respiration Rate",
    name: "Respiration",
  },
  {
    id: "metabolicQuotient",
    displayName: "Metabolic Quotient",
    name: "Metabolic quotient",
  },
  {
    id: "soilOrganicMatter",
    displayName: "Soil Organic Matter (LoI)",
    name: "OM LOI",
  },
  {
    id: "waterHoldingCapacity",
    displayName: "Water Holding Capacity",
    name: "WHC water content",
  },
  {
    id: "fieldWaterContent",
    displayName: "Field Water Content",
    name: "Field water content",
  },
];

const Page1Table: React.FC<Page1TableProps> = ({
  allTests,
  indicatorNamesAndIds,
  headers: visibleHeaders = page1Headers,
}) => {
  const gradient = tinygradient(IndicatorGradientColors);

  const findTrainedIndicator = (indicatorName: string) =>
    indicatorNamesAndIds.find((indicator) =>
      IndicatorNameMatches(indicator.name, indicatorName)
    )?.trainedIndicator;

  const getValueHeader = (header: PdfIndicatorHeader) => {
    if (header.id === "microbialBiomass") {
      return "Value in µgC/g";
    }
    if (header.id === "basalRespirationRate") {
      return "Value in\nµgCO₂-C/g/h";
    }
    if (header.id === "metabolicQuotient") {
      const unit = findTrainedIndicator(header.name)?.indicatorDefinition?.unit;
      return unit ? `Value in ${unit}` : "Value";
    }
    return "Value in %";
  };

  return (
    <div className={styles.reportTableContainer}>
      <div className={styles.tableWrapper}>
        <table className={styles.infoTable}>
          <thead>
            <tr>
              {/* Test Info Block Headers */}
              <th rowSpan={2} className={styles.tableHeader}>
                Sample Name
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Farm
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Field
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                What3Words
              </th>
              <th rowSpan={2} className={styles.tableHeader}>
                Date of Test
              </th>
              {/* Page1 Headers */}
              {visibleHeaders.map((header) => (
                <th key={header.id} className={styles.subHeader} colSpan={2}>
                  {header.displayName}
                </th>
              ))}
            </tr>
            <tr>
              {/* Subheaders for Page1, but none for the TestInfoBlock */}
              {visibleHeaders.map((header) => (
                <>
                  <th key={`${header.id}-value`} className={styles.subHeader}>
                    {getValueHeader(header)}
                  </th>
                  <th key={`${header.id}-index`} className={styles.subHeader}>
                    PES index
                  </th>
                </>
              ))}
            </tr>
          </thead>
          <tbody>
            {allTests.map((test, index) => (
              <tr key={test.id} className={styles.tableRow}>
                {/* Render the Test Info Block */}
                <td className={styles.tableCell}>{test.name}</td>
                <td className={styles.tableCell}>{test.farm?.name}</td>
                <td className={styles.tableCell}>{test.field?.name}</td>
                <td className={`${styles.tableCell} ${styles.noBreak}`}>
                  {test.location
                    ? `${test.location.what3Words?.words ?? "Unknown"}`
                    : "N/A"}
                </td>
                <td className={styles.tableCell}>
                  {test.createdAt
                    ? format(parseISO(test.createdAt), "dd/MM/yyyy")
                    : "N/A"}
                </td>

                {/* Render Page1 Indicator Values */}
                {visibleHeaders.map((header) => {
                  const { value } = GetDynamicValue(test, header.name);

                  const trainedIndicator = findTrainedIndicator(header.name);
                  const pesIndex = GetPESIndex(value, trainedIndicator);
                  const index = pesIndex
                    ? pesIndex.indices.names[pesIndex.indexValue]
                    : "N/A";
                  const divisor = pesIndex
                    ? pesIndex.indices.names.length - 1
                    : 0;
                  const gradientPosition = Math.min(
                    Math.max(
                      0,
                      divisor === 0 || !pesIndex
                        ? 0
                        : pesIndex.indexValue / divisor
                    ),
                    1
                  );
                  const indexColor =
                    index === "N/A"
                      ? undefined
                      : gradient
                          .rgbAt(gradientPosition)
                          .setAlpha(0.7)
                          .toHex8String();

                  return (
                    <React.Fragment key={header.id}>
                      {test.isFailure ? (
                        <>
                          <td className={styles.tableCell}>N/A</td>
                          <td className={styles.tableCell}>N/A</td>
                        </>
                      ) : (
                        <>
                          <td
                            key={`${header.id}-value`}
                            className={styles.tableCell}
                          >
                            {typeof value === "number"
                              ? value.toFixed(
                                  trainedIndicator?.indicatorDefinition
                                    ?.displayDecimalPlaces ?? 2
                                )
                              : value}
                          </td>
                          <td
                            key={`${header.id}-index`}
                            className={`${styles.tableCell} ${
                              indexColor &&
                              !gradient.rgbAt(gradientPosition).isLight()
                                ? styles.light
                                : ""
                            }`}
                            style={{ backgroundColor: indexColor }}
                          >
                            {index}
                          </td>
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Page1Table;
